import { motion } from 'framer-motion';
import { Video, Upload, Calendar, MessageCircle, LucideIcon } from 'lucide-react';

interface QuickAction {
  icon: LucideIcon;
  label: string;
  color: string;
  onClick?: () => void;
}

interface QuickActionsTileProps {
  actions?: QuickAction[];
}

/**
 * Quick Actions Tile - Fast access to common actions
 * From FIGMA Clubhouse 2.0 design
 */
export function QuickActionsTile({ actions }: QuickActionsTileProps) {
  // Default actions - can be customized via props
  const defaultActions: QuickAction[] = [
    { icon: Video, label: 'Record Swing', color: 'from-red-500 to-red-600' },
    { icon: Upload, label: 'Upload Video', color: 'from-blue-500 to-blue-600' },
    { icon: Calendar, label: 'Schedule Lesson', color: 'from-purple-500 to-purple-600' },
    { icon: MessageCircle, label: 'Ask Coach', color: 'from-emerald-500 to-emerald-600' },
  ];

  const quickActions = actions || defaultActions;

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)' }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-2xl shadow-md overflow-hidden h-full"
      data-figma-id="quick-actions-tile"
    >
      <div className="px-6 py-5 border-b border-slate-200">
        <h3 className="text-slate-900 mb-1">Quick Actions</h3>
        <p className="text-slate-600 text-sm">Jump right in</p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            // Add data attribute for first-time user guide (Record Swing button)
            const isRecordSwing = action.label === 'Record Swing';
            return (
              <motion.button
                key={`${action.label}-${index}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={action.onClick}
                data-guide-target={isRecordSwing ? 'record-swing-button' : undefined}
                className={`p-4 rounded-xl bg-gradient-to-br ${action.color} text-white shadow-md hover:shadow-lg transition-shadow flex flex-col items-center gap-2`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-sm text-center font-medium">{action.label}</span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

